type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
    level: LogLevel;
    message: string;
    meta?: unknown;
    timestamp: string;
}

const LOG_ENDPOINT = '/__client-log';
const LOG_TO_CONSOLE = import.meta.env.VITE_LOG_TO_CONSOLE === 'true';
/** File logging only works with the Vite dev/preview server — never in production builds. */
const LOG_TO_FILE =
    import.meta.env.DEV && import.meta.env.VITE_LOG_TO_FILE !== 'false';

const levelPriority: Record<LogLevel, number> = {
    debug: 10,
    info: 20,
    warn: 30,
    error: 40,
};

const minLevel: LogLevel =
    (import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined) ?? 'info';

function shouldLog(level: LogLevel): boolean {
    return levelPriority[level] >= levelPriority[minLevel];
}

function serializeArg(arg: unknown): string {
    if (arg instanceof Error) {
        return arg.stack || arg.message;
    }
    if (typeof arg === 'string') {
        return arg;
    }
    try {
        return JSON.stringify(arg);
    } catch {
        return String(arg);
    }
}

function formatArgs(args: unknown[]): { message: string; meta?: unknown } {
    if (args.length === 0) {
        return { message: '' };
    }

    const [first, ...rest] = args;
    const message = serializeArg(first);

    if (rest.length === 0) {
        return { message };
    }

    if (rest.length === 1 && typeof rest[0] === 'object' && rest[0] !== null && !Array.isArray(rest[0])) {
        return { message, meta: rest[0] };
    }

    return { message, meta: rest };
}

function sendToLogFile(entry: LogEntry): void {
    if (!LOG_TO_FILE || typeof fetch === 'undefined') {
        return;
    }

    void fetch(LOG_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
        keepalive: true,
    }).catch(() => {
        // File logging is best-effort (dev server / preview only).
    });
}

function write(level: LogLevel, args: unknown[]): void {
    if (!shouldLog(level)) {
        return;
    }

    const { message, meta } = formatArgs(args);
    const entry: LogEntry = {
        level,
        message,
        meta,
        timestamp: new Date().toISOString(),
    };

    if (LOG_TO_CONSOLE) {
        const fn =
            level === 'error'
                ? console.error
                : level === 'warn'
                  ? console.warn
                  : level === 'debug'
                    ? console.debug
                    : console.info;
        fn(`[${entry.timestamp}] [${level}]`, message, meta ?? '');
    }

    sendToLogFile(entry);
}

export const logger = {
    debug: (...args: unknown[]) => write('debug', args),
    info: (...args: unknown[]) => write('info', args),
    warn: (...args: unknown[]) => write('warn', args),
    error: (...args: unknown[]) => write('error', args),
};

let consoleRedirectInstalled = false;

export function installConsoleRedirect(): void {
    if (consoleRedirectInstalled || typeof console === 'undefined') {
        return;
    }

    consoleRedirectInstalled = true;

    const original = {
        log: console.log.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        debug: console.debug.bind(console),
    };

    console.log = (...args: unknown[]) => {
        write('info', args);
        if (LOG_TO_CONSOLE) original.log(...args);
    };
    console.info = (...args: unknown[]) => {
        write('info', args);
        if (LOG_TO_CONSOLE) original.info(...args);
    };
    console.warn = (...args: unknown[]) => {
        write('warn', args);
        if (LOG_TO_CONSOLE) original.warn(...args);
    };
    console.error = (...args: unknown[]) => {
        write('error', args);
        if (LOG_TO_CONSOLE) original.error(...args);
    };
    console.debug = (...args: unknown[]) => {
        write('debug', args);
        if (LOG_TO_CONSOLE) original.debug(...args);
    };
}
